'use client';

import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import kebabCase from 'lodash/kebabCase';

import { DEFAULT_DATE_SEPARATOR } from '@/components/ui/hotels/date-formatting';
import { ClientSafeConfig } from '@/lib/types/tenant';

export type { ClientSafeConfig } from '@/lib/types/tenant';

type TranslationOverride = ClientSafeConfig['translations'];

interface ConfigContextType {
  config: ClientSafeConfig;
  setTranslations: (translations: TranslationOverride) => void;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
  config: ClientSafeConfig;
}

function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce((acc, key) => (acc as Record<string, unknown>)?.[key], obj);
}

export function ConfigProvider({ children, config }: ConfigProviderProps) {
  // Only the translations are ever swapped at runtime (suggestions following the page's
  // destination), so they are overlaid rather than held as a copy of the whole config — a copy
  // would freeze every other field at its first-render value.
  const [translations, setTranslations] = useState<TranslationOverride>();
  const value = useMemo(
    () => ({
      config: translations
        ? { ...config, translations: { ...config.translations, ...translations } }
        : config,
      setTranslations,
    }),
    [config, translations],
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ClientSafeConfig {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context.config;
}

export function useSetTranslations(): (translations: TranslationOverride) => void {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useSetTranslations must be used within a ConfigProvider');
  }
  return context.setTranslations;
}

export function useConfigValue<T = unknown>(keyPath: string): T | null {
  const context = useContext(ConfigContext);
  const config = context?.config;
  return useMemo(
    () => (config ? ((getByPath(config, keyPath) ?? null) as T | null) : null),
    [config, keyPath],
  );
}

export function useTenantKey(): string {
  return useConfig().tenantKey;
}

/** This tenant's day/month separator. */
export function useDateSeparator(): string {
  return useConfigValue<string>('meta.dateSeparator') ?? DEFAULT_DATE_SEPARATOR;
}

/** The kebab-cased tenant name used as the backend path segment (`/{tenantName}/...`). */
export function useTenantName(): string {
  const tenantKey = useTenantKey();
  return useMemo(() => kebabCase(tenantKey), [tenantKey]);
}
